import { apiSlice } from "./api";

// Data transformation utilities
export const transformBadgeData = (backendBadge) => {
  const { badge, progress, modules } = backendBadge;
  
  return {
    // Flatten badge structure for component consumption
    badge_id: badge.id,
    name: badge.name,
    badge_type: badge.badge_type,
    description: badge.description,
    
    // Progress and status
    is_earned: progress.is_earned,
    is_eligible: progress.earning_progress?.progress_percentage >= 100,
    earned_at: progress.earned_at,
    
    // Module information
    completed_modules: progress.completed_modules,
    total_modules: progress.total_modules,
    modules: modules || [],
    
    // Digital badge data (if earned)
    credential_id: progress.is_earned ? progress.credential_id : null,
    verification_url: progress.is_earned ? progress.verification_url : null,
    
    // Requirements for unearned badges
    requirements: !progress.is_earned ? formatRequirements(badge, progress) : null,
    
    // Keep original structure for compatibility
    badge,
    progress,
    
    // Legacy compatibility - wrap earned badge data
    earned_badges: progress.is_earned ? [{
      badge_id: badge.id,
      name: badge.name,
      credential_id: progress.credential_id,
      verification_url: progress.verification_url,
      issued_date: progress.earned_at,
      badge_type: badge.badge_type,
      description: badge.description
    }] : []
  };
};

export const formatRequirements = (badge, progress) => {
  const requirements = [];
  
  if (progress.earning_progress) {
    const { completed_modules = 0, total_modules = 0 } = progress.earning_progress;
    if (total_modules > 0) {
      requirements.push(`Complete ${total_modules - completed_modules} more training modules`);
    }
  }
  
  if (badge.requirements) {
    requirements.push(...badge.requirements);
  }
  
  return requirements;
};

export const calculateBadgeSummary = (badges) => {
  const earned = badges.filter(b => b.progress.is_earned);
  const available = badges.filter(b => !b.progress.is_earned && (b.progress.earning_progress?.progress_percentage >= 100));
  
  return {
    total_badges: badges.length,
    earned_count: earned.length,
    available_count: available.length,
    completion_percentage: badges.length > 0 ? Math.round((earned.length / badges.length) * 100) : 0
  };
};

export const newBadgeApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Use credentialing dashboard as primary badge data source
    getBadgeDashboard: builder.query({
      query: () => ({
        url: "api/credentialing/dashboard/",
        method: "GET",
      }),
      transformResponse: (response) => {
        // Transform backend response to frontend format
        const badges = response.data?.badges || [];
        return {
          badges: badges.map(transformBadgeData),
          summary: calculateBadgeSummary(badges),
          rawData: response.data
        };
      },
      providesTags: ["Badges", "Training", "CredentialingDashboard"]
    }),

    // Standalone badge eligibility for non-dashboard contexts
    getBadgeEligibility: builder.query({
      query: () => "api/badges/eligibility/",
      transformResponse: (response) => response.badges || [],
      providesTags: ["BadgeEligibility"]
    }),

    // Badge collection with immediate response
    collectBadge: builder.mutation({
      query: ({ badgeId }) => ({
        url: "api/badges/collect/",
        method: "POST",
        body: { badge_id: badgeId }
      }),
      invalidatesTags: ["Badges", "Training", "CredentialingDashboard", "BadgeEligibility"]
    }),

    // Generate digital badge (for earned badges)
    generateDigitalBadge: builder.mutation({
      query: ({ badgeId, forceRegenerate = false }) => ({
        url: "api/badges/generate/",
        method: "POST",
        body: {
          badge_id: badgeId,
          force_regenerate: forceRegenerate
        }
      }),
      invalidatesTags: ["Badges", "Training", "CredentialingDashboard"]
    }),

    // LinkedIn sharing with proper response parsing
    getLinkedInSharingData: builder.mutation({
      query: ({ credentialId }) => ({
        url: "api/badges/share/linkedin/",
        method: "POST", 
        body: { credential_id: credentialId }
      }),
      transformResponse: (response) => ({
        ...response.sharing_data,
        credentialId: response.credential_id,
        badgeName: response.name,
        sharing_data: response.sharing_data
      })
    }),

    trackLinkedInShare: builder.mutation({
      query: ({ credentialId, shareMethod = 'direct' }) => ({
        url: "api/badges/track-linkedin-share/",
        method: "POST",
        body: { credential_id: credentialId, share_method: shareMethod }
      }),
      invalidatesTags: ["Badges"]
    }),

    // Get badge verification data (public endpoint)
    getBadgeVerification: builder.query({
      query: (credentialId) => ({
        url: `api/badges/verify/${credentialId}/`,
        method: "GET",
        headers: {
          'Accept': 'application/json'
        }
      }),
    }),

    // Download badge as JSON-LD
    downloadBadgeJsonLd: builder.query({
      query: (credentialId) => ({
        url: `api/badges/verify/${credentialId}/json-ld/`,
        method: "GET",
        headers: {
          'Accept': 'application/ld+json'
        }
      }),
    }),

    // Verify badge ownership with email
    verifyBadgeOwnership: builder.mutation({
      query: ({ credentialId, email }) => ({
        url: `api/badges/${credentialId}/verify-ownership/`,
        method: "POST",
        body: { email },
        headers: {
          'Content-Type': 'application/json'
        }
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetBadgeDashboardQuery,
  useGetBadgeEligibilityQuery,
  useCollectBadgeMutation,
  useGenerateDigitalBadgeMutation,
  useGetLinkedInSharingDataMutation,
  useTrackLinkedInShareMutation,
  useGetBadgeVerificationQuery,
  useLazyDownloadBadgeJsonLdQuery,
  useVerifyBadgeOwnershipMutation,
} = newBadgeApi;